import {ReactNode, RefObject, useCallback, useEffect, useMemo, useRef, useState} from "react";
import {css} from "@panda/css";

import {getCompactRegionPillLabels, getVisibleRegionPillLabels} from "../utils/get_visible_region_pill_labels";

const pillsGapFallbackPx = 4;

interface IRegionPillsOverflowMeasurement {
    availableWidth: number;
    counterPillWidths: Record<number, number>;
    gap: number;
    regionPillWidths: number[];
}

interface IProps {
    regionLabels: string[];
    wrapperClassName: string;
    renderPill: (label: string, options: {isVisible: boolean; index: number}) => ReactNode;
    availableWidthElementRef?: RefObject<HTMLElement | null>;
    dataTestId?: string;
    gapFallbackPx?: number;
    reservedWidthElementRef?: RefObject<HTMLElement | null>;
}

export const RegionPillsOverflow = (props: IProps) => {
    const pillsWrapperRef = useRef<HTMLDivElement>(null);
    const measurementWrapperRef = useRef<HTMLDivElement>(null);
    const [measurement, setMeasurement] = useState<IRegionPillsOverflowMeasurement | null>(null);

    const counterHiddenCounts = useMemo(() => props.regionLabels.slice(1).map((_regionLabel, index) => index + 1), [props.regionLabels]);

    const measurePills = useCallback(() => {
        const pillsWrapper = pillsWrapperRef.current;
        const measurementWrapper = measurementWrapperRef.current;

        if (!pillsWrapper || !measurementWrapper) {
            return;
        }

        const regionPillWidths = Array.from(measurementWrapper.querySelectorAll<HTMLElement>("[data-region-pill-measurement]")).map(
            (element) => element.getBoundingClientRect().width
        );
        const counterPillWidths = Array.from(measurementWrapper.querySelectorAll<HTMLElement>("[data-region-counter-pill-measurement]")).reduce<
            Record<number, number>
        >((acc, element) => {
            const hiddenCount = Number(element.dataset.regionCounterPillMeasurement);

            if (!Number.isNaN(hiddenCount)) {
                acc[hiddenCount] = element.getBoundingClientRect().width;
            }

            return acc;
        }, {});
        const computedStyle = window.getComputedStyle(pillsWrapper);
        const gap = parseFloat(computedStyle.columnGap || computedStyle.gap) || props.gapFallbackPx || pillsGapFallbackPx;
        const nextMeasurement = {
            availableWidth: getAvailableWidth({
                availableWidthElement: props.availableWidthElementRef?.current,
                fallbackElement: pillsWrapper,
                gapFallbackPx: props.gapFallbackPx,
                reservedWidthElement: props.reservedWidthElementRef?.current
            }),
            counterPillWidths,
            gap,
            regionPillWidths
        };

        setMeasurement((prevMeasurement) => (arePillMeasurementsEqual(prevMeasurement, nextMeasurement) ? prevMeasurement : nextMeasurement));
    }, [props.availableWidthElementRef, props.gapFallbackPx, props.regionLabels, props.reservedWidthElementRef]);

    useEffect(() => {
        measurePills();

        const pillsWrapper = pillsWrapperRef.current;

        if (!pillsWrapper) {
            return;
        }

        const resizeObserver = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measurePills) : null;
        const observedElements = [pillsWrapper, props.availableWidthElementRef?.current, props.reservedWidthElementRef?.current].filter(
            (element, index, elements): element is HTMLElement => !!element && elements.indexOf(element) === index
        );
        observedElements.forEach((element) => resizeObserver?.observe(element));
        window.addEventListener("resize", measurePills);

        return () => {
            resizeObserver?.disconnect();
            window.removeEventListener("resize", measurePills);
        };
    }, [measurePills, props.availableWidthElementRef, props.reservedWidthElementRef]);

    const visibleRegionPillLabels = useMemo(() => {
        if (!measurement) {
            return getCompactRegionPillLabels(props.regionLabels);
        }

        return getVisibleRegionPillLabels({
            ...measurement,
            regionLabels: props.regionLabels
        });
    }, [measurement, props.regionLabels]);

    return (
        <>
            <div ref={pillsWrapperRef} className={props.wrapperClassName} data-testid={props.dataTestId} data-region-pills-overflow-wrapper="true">
                {visibleRegionPillLabels.map((regionPillLabel, index) => props.renderPill(regionPillLabel, {isVisible: true, index}))}
            </div>

            <div ref={measurementWrapperRef} className={measurementPillsWrapper} aria-hidden="true">
                {props.regionLabels.map((regionPillLabel, index) => (
                    <div key={`${regionPillLabel}-${index.toString()}`} data-region-pill-measurement={index}>
                        {props.renderPill(regionPillLabel, {isVisible: false, index})}
                    </div>
                ))}
                {counterHiddenCounts.map((hiddenCount, index) => (
                    <div key={`+${hiddenCount.toString()}`} data-region-counter-pill-measurement={hiddenCount}>
                        {props.renderPill(`+${hiddenCount.toString()}`, {isVisible: false, index})}
                    </div>
                ))}
            </div>
        </>
    );
};

const getAvailableWidth = (props: {
    availableWidthElement?: HTMLElement | null;
    fallbackElement: HTMLElement;
    gapFallbackPx?: number;
    reservedWidthElement?: HTMLElement | null;
}) => {
    if (!props.availableWidthElement) {
        return props.fallbackElement.getBoundingClientRect().width;
    }

    const reservedWidth = props.reservedWidthElement?.getBoundingClientRect().width ?? 0;
    const availableElementStyle = window.getComputedStyle(props.availableWidthElement);
    const reservedGap = props.reservedWidthElement
        ? parseFloat(availableElementStyle.columnGap || availableElementStyle.gap) || props.gapFallbackPx || pillsGapFallbackPx
        : 0;

    return Math.max(0, props.availableWidthElement.getBoundingClientRect().width - reservedWidth - reservedGap);
};

const arePillMeasurementsEqual = (prevMeasurement: IRegionPillsOverflowMeasurement | null, nextMeasurement: IRegionPillsOverflowMeasurement) => {
    if (!prevMeasurement) {
        return false;
    }

    return (
        prevMeasurement.availableWidth === nextMeasurement.availableWidth &&
        prevMeasurement.gap === nextMeasurement.gap &&
        areNumberArraysEqual(prevMeasurement.regionPillWidths, nextMeasurement.regionPillWidths) &&
        areNumberRecordsEqual(prevMeasurement.counterPillWidths, nextMeasurement.counterPillWidths)
    );
};

const areNumberArraysEqual = (left: number[], right: number[]) => left.length === right.length && left.every((value, index) => value === right[index]);

const areNumberRecordsEqual = (left: Record<number, number>, right: Record<number, number>) => {
    const leftKeys = Object.keys(left);
    const rightKeys = Object.keys(right);

    return leftKeys.length === rightKeys.length && leftKeys.every((key) => left[Number(key)] === right[Number(key)]);
};

const measurementPillsWrapper = css({
    position: "fixed",
    top: 0,
    left: "-10000px",
    zIndex: -1,
    display: "flex",
    gap: 0.5,
    visibility: "hidden",
    pointerEvents: "none",
    whiteSpace: "nowrap"
});
import {ReactNode, RefObject, useCallback, useEffect, useMemo, useRef, useState} from "react";
import {css} from "@panda/css";

import {getCompactRegionPillLabels, getVisibleRegionPillLabels} from "../utils/get_visible_region_pill_labels";

const pillsGapFallbackPx = 4;

interface IRegionPillsOverflowMeasurement {
    availableWidth: number;
    counterPillWidths: Record<number, number>;
    gap: number;
    regionPillWidths: number[];
}

interface IProps {
    regionLabels: string[];
    wrapperClassName: string;
    renderPill: (label: string, options: {isVisible: boolean; index: number}) => ReactNode;
    availableWidthElementRef?: RefObject<HTMLElement | null>;
    dataTestId?: string;
    gapFallbackPx?: number;
    reservedWidthElementRef?: RefObject<HTMLElement | null>;
}

export const RegionPillsOverflow = (props: IProps) => {
    const pillsWrapperRef = useRef<HTMLDivElement>(null);
    const measurementWrapperRef = useRef<HTMLDivElement>(null);
    const [measurement, setMeasurement] = useState<IRegionPillsOverflowMeasurement | null>(null);

    const counterHiddenCounts = useMemo(() => props.regionLabels.slice(1).map((_regionLabel, index) => index + 1), [props.regionLabels]);

    const measurePills = useCallback(() => {
        const pillsWrapper = pillsWrapperRef.current;
        const measurementWrapper = measurementWrapperRef.current;

        if (!pillsWrapper || !measurementWrapper) {
            return;
        }

        const regionPillWidths = Array.from(measurementWrapper.querySelectorAll<HTMLElement>("[data-region-pill-measurement]")).map(
            (element) => element.getBoundingClientRect().width
        );
        const counterPillWidths = Array.from(measurementWrapper.querySelectorAll<HTMLElement>("[data-region-counter-pill-measurement]")).reduce<
            Record<number, number>
        >((acc, element) => {
            const hiddenCount = Number(element.dataset.regionCounterPillMeasurement);

            if (!Number.isNaN(hiddenCount)) {
                acc[hiddenCount] = element.getBoundingClientRect().width;
            }

            return acc;
        }, {});
        const computedStyle = window.getComputedStyle(pillsWrapper);
        const gap = parseFloat(computedStyle.columnGap || computedStyle.gap) || props.gapFallbackPx || pillsGapFallbackPx;
        const nextMeasurement = {
            availableWidth: getAvailableWidth({
                availableWidthElement: props.availableWidthElementRef?.current,
                fallbackElement: pillsWrapper,
                gapFallbackPx: props.gapFallbackPx,
                reservedWidthElement: props.reservedWidthElementRef?.current
            }),
            counterPillWidths,
            gap,
            regionPillWidths
        };

        setMeasurement((prevMeasurement) => (arePillMeasurementsEqual(prevMeasurement, nextMeasurement) ? prevMeasurement : nextMeasurement));
    }, [props.availableWidthElementRef, props.gapFallbackPx, props.regionLabels, props.reservedWidthElementRef]);

    useEffect(() => {
        measurePills();

        const pillsWrapper = pillsWrapperRef.current;

        if (!pillsWrapper) {
            return;
        }

        const resizeObserver = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measurePills) : null;
        const observedElements = [pillsWrapper, props.availableWidthElementRef?.current, props.reservedWidthElementRef?.current].filter(
            (element, index, elements): element is HTMLElement => !!element && elements.indexOf(element) === index
        );
        observedElements.forEach((element) => resizeObserver?.observe(element));
        window.addEventListener("resize", measurePills);

        return () => {
            resizeObserver?.disconnect();
            window.removeEventListener("resize", measurePills);
        };
    }, [measurePills, props.availableWidthElementRef, props.reservedWidthElementRef]);

    const visibleRegionPillLabels = useMemo(() => {
        if (!measurement) {
            return getCompactRegionPillLabels(props.regionLabels);
        }

        return getVisibleRegionPillLabels({
            ...measurement,
            regionLabels: props.regionLabels
        });
    }, [measurement, props.regionLabels]);

    return (
        <>
            <div ref={pillsWrapperRef} className={props.wrapperClassName} data-testid={props.dataTestId} data-region-pills-overflow-wrapper="true">
                {visibleRegionPillLabels.map((regionPillLabel, index) => props.renderPill(regionPillLabel, {isVisible: true, index}))}
            </div>

            <div ref={measurementWrapperRef} className={measurementPillsWrapper} aria-hidden="true">
                {props.regionLabels.map((regionPillLabel, index) => (
                    <div key={`${regionPillLabel}-${index.toString()}`} data-region-pill-measurement={index}>
                        {props.renderPill(regionPillLabel, {isVisible: false, index})}
                    </div>
                ))}
                {counterHiddenCounts.map((hiddenCount, index) => (
                    <div key={`+${hiddenCount.toString()}`} data-region-counter-pill-measurement={hiddenCount}>
                        {props.renderPill(`+${hiddenCount.toString()}`, {isVisible: false, index})}
                    </div>
                ))}
            </div>
        </>
    );
};

const getAvailableWidth = (props: {
    availableWidthElement?: HTMLElement | null;
    fallbackElement: HTMLElement;
    gapFallbackPx?: number;
    reservedWidthElement?: HTMLElement | null;
}) => {
    if (!props.availableWidthElement) {
        return props.fallbackElement.getBoundingClientRect().width;
    }

    const reservedWidth = props.reservedWidthElement?.getBoundingClientRect().width ?? 0;
    const availableElementStyle = window.getComputedStyle(props.availableWidthElement);
    const reservedGap = props.reservedWidthElement
        ? parseFloat(availableElementStyle.columnGap || availableElementStyle.gap) || props.gapFallbackPx || pillsGapFallbackPx
        : 0;

    return Math.max(0, props.availableWidthElement.getBoundingClientRect().width - reservedWidth - reservedGap);
};

const arePillMeasurementsEqual = (prevMeasurement: IRegionPillsOverflowMeasurement | null, nextMeasurement: IRegionPillsOverflowMeasurement) => {
    if (!prevMeasurement) {
        return false;
    }

    return (
        prevMeasurement.availableWidth === nextMeasurement.availableWidth &&
        prevMeasurement.gap === nextMeasurement.gap &&
        areNumberArraysEqual(prevMeasurement.regionPillWidths, nextMeasurement.regionPillWidths) &&
        areNumberRecordsEqual(prevMeasurement.counterPillWidths, nextMeasurement.counterPillWidths)
    );
};

const areNumberArraysEqual = (left: number[], right: number[]) => left.length === right.length && left.every((value, index) => value === right[index]);

const areNumberRecordsEqual = (left: Record<number, number>, right: Record<number, number>) => {
    const leftKeys = Object.keys(left);
    const rightKeys = Object.keys(right);

    return leftKeys.length === rightKeys.length && leftKeys.every((key) => left[Number(key)] === right[Number(key)]);
};

const measurementPillsWrapper = css({
    position: "fixed",
    top: 0,
    left: "-10000px",
    zIndex: -1,
    display: "flex",
    gap: 0.5,
    visibility: "hidden",
    pointerEvents: "none",
    whiteSpace: "nowrap"
});
